import { useState, useCallback, useEffect, useRef, ReactElement, UIEvent } from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useVirtualizer } from '@tanstack/react-virtual';

import { LogsTableRow } from './LogsTableRow';
import { LogsTableEvent } from './types';

export type { LogsTableEvent } from './types';
export { LogsTableToolbar } from './LogsTableToolbar';

const RootContainer = styled(Box)({
  height: '100%',
  position: 'relative',
});

const ScrollContainer = styled('div')({
  position: 'absolute',
  inset: 0,
  overflow: 'auto',
  overflowAnchor: 'none',
});

const VirtualList = styled('div')({
  width: '100%',
  position: 'relative',
});

const VirtualRow = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
});

enum LoadDirection {
  FORWARD = 'FORWARD',
  BACKWARD = 'BACKWARD',
  FORWARD_END = 'FORWARD_END',
}

const NEAR_BOTTOM_THRESHOLD_PX = 32;

export interface LogsTableProps {
  events: LogsTableEvent[];
  onLoadForward: () => unknown;
  onLoadBackward: () => unknown;
  onViewGroup?: (groupName: string) => unknown;
}

export const LogsTable = ({ events, onLoadForward, onLoadBackward, onViewGroup }: LogsTableProps): ReactElement => {
  // Direction to decide how to scroll when we have new portion of logs
  const [lastDirection, setLastDirection] = useState(LoadDirection.FORWARD_END);

  // track the very first scroll event (first scroll event is skipped)
  const [scrollInitialized, setScrollInitialized] = useState(false);

  // Loading guard (forward or backward). A ref, not state, so flipping it never re-runs effects — otherwise the forward-load effect would loop forever.
  const pageLoadingRef = useRef(false);

  const [oldLogsLength, setOldLogsLength] = useState(0);

  // Whether the user is scrolled near the bottom: only auto-scroll on new logs when they are, so we don't yank the view from someone reading older logs. Defaults to true so the first load lands at the bottom.
  const isNearBottomRef = useRef(true);

  const scrollElementRef = useRef<HTMLDivElement | null>(null);

  const virtualizer = useVirtualizer({
    count: events.length,
    getScrollElement: () => scrollElementRef.current,
    estimateSize: () => 20,
    overscan: 10,
    getItemKey: (index) => events[index]?.id ?? index,
  });

  // Callers pass onLoadForward/onLoadBackward as inline functions with a new identity every render. Read them through a ref (always fresh, never a dependency) to keep loadMoreRows/onScroll stable, so the effect below only re-runs on genuine range/count changes.
  const onLoadForwardRef = useRef(onLoadForward);
  onLoadForwardRef.current = onLoadForward;

  const onLoadBackwardRef = useRef(onLoadBackward);
  onLoadBackwardRef.current = onLoadBackward;

  const loadMoreRows = useCallback(async () => {
    if (pageLoadingRef.current) return;

    pageLoadingRef.current = true;

    await onLoadForwardRef.current();

    setLastDirection(LoadDirection.FORWARD);
    pageLoadingRef.current = false;
  }, []);

  const onScroll = useCallback(
    async (e: UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      isNearBottomRef.current = scrollHeight - (scrollTop + clientHeight) <= NEAR_BOTTOM_THRESHOLD_PX;

      if (!scrollInitialized) {
        setScrollInitialized(true);
        return;
      }

      if (pageLoadingRef.current || scrollTop !== 0) return;

      pageLoadingRef.current = true;

      await onLoadBackwardRef.current();

      setLastDirection(LoadDirection.BACKWARD);
      pageLoadingRef.current = false;
    },
    [scrollInitialized],
  );

  // Trigger the next page of forward-loaded logs once the last rendered row comes into view
  const virtualItems = virtualizer.getVirtualItems();
  const lastVirtualItemIndex = virtualItems[virtualItems.length - 1]?.index;

  useEffect(() => {
    if (lastVirtualItemIndex === undefined) {
      return;
    }

    if (lastVirtualItemIndex >= events.length - 1) {
      loadMoreRows();
    }
  }, [lastVirtualItemIndex, events.length, loadMoreRows]);

  // Scroll to the right row when new logs are appended in the live mode/scroll,
  // or when logs are prepended in scroll mode
  useEffect(() => {
    if (pageLoadingRef.current) return;
    if (oldLogsLength === events.length) return;

    if (
      (lastDirection === LoadDirection.FORWARD_END || lastDirection === LoadDirection.FORWARD) &&
      isNearBottomRef.current
    ) {
      virtualizer.scrollToIndex(events.length - 1, { align: 'end' });
    }

    if (lastDirection === LoadDirection.BACKWARD) {
      virtualizer.scrollToIndex(events.length - oldLogsLength, { align: 'start' });
    }

    setOldLogsLength(events.length);
  }, [events.length, lastDirection, oldLogsLength, virtualizer]);

  return (
    <RootContainer>
      <ScrollContainer ref={scrollElementRef} onScroll={onScroll}>
        <VirtualList style={{ height: virtualizer.getTotalSize() }}>
          {virtualItems.map((virtualRow) => {
            const event = events[virtualRow.index];

            if (!event) {
              return null;
            }

            return (
              <VirtualRow
                key={String(virtualRow.key)}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                style={{ transform: `translateY(${virtualRow.start}px)` }}
              >
                <LogsTableRow event={event} onViewGroup={onViewGroup} />
              </VirtualRow>
            );
          })}
        </VirtualList>
      </ScrollContainer>
    </RootContainer>
  );
};
